import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import getAppConfig from '../../utils/appConfig';
import { NotificationsActions } from '../../app/common/notifications/notificationsActions';
import { history } from '../../utils/history';
import store from '../../utils/store';
import {
    FORBIDDEN_ROUTE,
    NOT_FOUND_ROUTE,
    SERVICE_UNAVAILABLE,
    UNAUTHORISED_ROUTE,
    UNEXPECTED_ROUTE,
} from './errorRoutes';
import { TextHelpers } from '../../utils/textHelpers';
import { getReAuthUrl } from '../../utils/url';

const COUNTDOWN_TIME = 3000;

export default class ErrorRedirectService {
    public static takeToLogout(redirectUrl?: string) {
        let post_logout_uri: string;
        if (redirectUrl) {
            const encodedRedirectUrl = encodeURIComponent(redirectUrl);
            post_logout_uri = `?post_logout_uri=${encodedRedirectUrl}`;
        } else {
            post_logout_uri = '';
        }
        getAppConfig().then(async (appConfig) => {
            window.location.href = `${appConfig.CALLBACK_URL}logout${post_logout_uri}`;
        });
    }

    public static unexpected() {
        history.push(UNEXPECTED_ROUTE.path);
    }

    public static unauthorised() {
        history.push(UNAUTHORISED_ROUTE.path);
    }

    public static forbidden() {
        history.push(FORBIDDEN_ROUTE.path);
    }

    public static notFound() {
        history.push(NOT_FOUND_ROUTE.path);
    }

    public static unavailable() {
        history.push(SERVICE_UNAVAILABLE.path);
    }

    public static noLinkage() {
        const reAuthUrl = getReAuthUrl();
        reAuthUrl.searchParams.append('linkage', 'true');
        window.location.assign(reAuthUrl.toString());
    }

    // remember current path and redirect to login page, after user successfully login, redirect to the remembered path
    public static reAuth() {
        const reAuthUrl = getReAuthUrl();

        store.dispatch(
            NotificationsActions.showRedirectWarning(
                reAuthUrl.toString(),
                'notifications.redirect.reason.expired',
            ),
        );
        setTimeout(() => {
            window.location.href = reAuthUrl.toString();
        }, COUNTDOWN_TIME);
    }

    public static notLoggedIn() {
        const clientConfig = store.getState().app.clientConfig;
        const clientId = webStorage.getActiveClientId();
        const prefix = window.location.pathname.includes('/profile') ? '/profile' : '';
        const lastVisitedPath = TextHelpers.setLastVisitedPathHandle(
            window.location.pathname,
            prefix,
        );

        if (!store.getState().notifications.redirecting) {
            if (clientId && clientConfig && clientConfig.tokenExpirationUrl) {
                const redirectUrl = new URL(clientConfig.tokenExpirationUrl);
                redirectUrl.searchParams.append('last_visited_path', lastVisitedPath);

                store.dispatch(
                    NotificationsActions.showRedirectWarning(
                        redirectUrl.toString(),
                        'notifications.redirect.reason.expired',
                    ),
                );

                setTimeout(() => {
                    ErrorRedirectService.takeToLogout(redirectUrl.toString());
                }, COUNTDOWN_TIME);
            } else {
                ErrorRedirectService.noClientId(lastVisitedPath);
            }
        }
    }

    public static noClientId(lastVisitedPath?: string) {
        if (!store.getState().notifications.redirecting) {
            const appConfig = store.getState().app.appConfig;

            if (appConfig.NO_CLIENT_ID_URL) {
                const noClientIdUrlWithState = new URL(appConfig.NO_CLIENT_ID_URL);
                if (lastVisitedPath) {
                    noClientIdUrlWithState.searchParams.append(
                        'last_visited_path',
                        lastVisitedPath,
                    );
                }
                window.location.assign(noClientIdUrlWithState.toString());
            } else {
                ErrorRedirectService.forbidden();
            }
        }
    }

    public static authError(errorCode: string, operation?: string) {
        const clientConfig =
            store.getState().app.appConfig.ENABLE_2FA_OMNI + '&r10_rejection=' + errorCode;
        const clientId = webStorage.getActiveClientId();
        const prefix = window.location.pathname.includes('/profile') ? '/profile' : '';
        let lastVisitedPath = TextHelpers.setLastVisitedPathHandle(
            window.location.pathname,
            prefix,
        );

        // Preserve user's intended action as query parameter
        if (operation) {
            const normalizedPath = lastVisitedPath.startsWith('/')
                ? lastVisitedPath
                : '/' + lastVisitedPath;
            const url = new URL(normalizedPath, window.location.origin);
            url.searchParams.set('pending_action', operation.toLowerCase());
            lastVisitedPath = url.pathname + url.search;
        }

        if (!store.getState().notifications.redirecting) {
            if (clientId && clientConfig) {
                const redirectUrl = new URL(clientConfig);
                // Use .set() to replace hardcoded state from config (if present) with dynamic state.
                // This allows pending_action to be preserved while keeping config simple with default state.
                redirectUrl.searchParams.set('state', lastVisitedPath);
                window.location.href = redirectUrl.toString();
            } else {
                ErrorRedirectService.noClientId(lastVisitedPath);
            }
        }
    }
}
